import i18next from "i18next";

/**
 * 언어별 폰트 스택.
 *
 * 한자(CJK 통합한자)는 언어가 달라도 코드포인트가 같다(예: 平 = U+5E73). 어떤 자형으로
 * 보이는지는 오직 **어느 폰트가 먼저 그 글자를 가지고 있느냐**로 결정된다. Noto Sans KR /
 * JP / SC 는 모두 통합한자를 통째로 담고 있어서, 맨 앞 폰트가 글자를 가지면 브라우저는
 * 뒤 폰트로 절대 넘어가지 않는다. 즉 "KR, JP, SC" 순서면 일본어 화면도 한국 자형으로
 * 그려진다(일본 사용자가 平 의 가운데 두 획 방향이 다르다고 지적한 건이 이것).
 *
 * 따라서 해당 언어의 폰트를 반드시 **맨 앞**에 둔다. 라틴 문자·숫자는 어느 Noto 를 써도
 * 거의 동일하므로, 자형 구분이 없는 언어(en/ko/vi)는 기존대로 KR 을 앞에 두어
 * 영문·숫자·한글이 한 페이스에서 나오게 한다.
 */
const FONT_STACK_KR_FIRST = '"Noto Sans KR", "Noto Sans JP", "Noto Sans SC", sans-serif';

const FONT_STACK_BY_LANG = {
  "ja-JP": '"Noto Sans JP", "Noto Sans KR", "Noto Sans SC", sans-serif',
  "zh-TW": '"Noto Sans TC", "Noto Sans SC", "Noto Sans KR", sans-serif',
  "zh-CN": '"Noto Sans SC", "Noto Sans TC", "Noto Sans KR", sans-serif',
};

/**
 * CSS 변수 이름. SCSS/MUI/SVG 등 CSS 가 닿는 곳은 전부 이 변수를 쓴다.
 * (canvas 로 그리는 ag-charts sparkline 만 예외 — 아래 getFontFamily() 참고)
 */
export const FONT_FAMILY_VAR = "--app-font-family";

/** CSS 에서 쓰는 값. `sx={{ fontFamily: FONT_FAMILY_CSS }}` 형태로 사용. */
export const FONT_FAMILY_CSS = `var(${FONT_FAMILY_VAR})`;

/**
 * 해석된 폰트 스택 문자열.
 *
 * canvas 렌더러(ag-charts sparkline 등)는 `var()` 를 해석하지 못하고 문자열 그대로
 * `ctx.font` 에 넣기 때문에, 그런 곳에서만 이 함수를 쓴다. CSS 가 닿는 곳은
 * FONT_FAMILY_CSS 를 쓸 것.
 */
export const getFontFamily = (lang = i18next.language) =>
    FONT_STACK_BY_LANG[lang] || FONT_STACK_KR_FIRST;

/**
 * <html> 의 lang 속성과 폰트 CSS 변수를 현재 언어에 맞춘다.
 *
 * lang 속성은 폰트 선택 외에도 브라우저의 줄바꿈 규칙·읽기 도구·`:lang()` 선택자가
 * 참조하므로 함께 갱신한다. (기존에는 index.html 의 `lang="en"` 이 끝까지 남아 있었다.)
 */
export const applyDocumentLanguage = (lang) => {
  const code = lang || i18next.language;
  if (!code) return;

  document.documentElement.lang = code;
  document.documentElement.style.setProperty(FONT_FAMILY_VAR, getFontFamily(code));
};

/* ────────────────────────────────────────────────────────────────────────────
 * 폰트 크기 스케일 (Big / Small)
 *
 * 규칙·역할표·마이그레이션 계획은 `saas-web/.claude/skills/typography/SKILL.md` 가 정본이다.
 * 값 자체는 App.scss 의 `:root[data-font-scale]` 에 있다 — JS 가 뜨기 전에도 글자가
 * 브라우저 기본 16px 로 튀지 않아야 하므로 CSS 가 원본이고 여기서는 참조만 한다.
 * ──────────────────────────────────────────────────────────────────────────── */

/** localStorage 키. 언어 설정(`lang`)과 같은 관행. */
export const FONT_SCALE_KEY = "fontScale";

export const fontScales = {
  SMALL: "small",   // 기본값 — 기존 화면 밀도와 동일
  BIG: "big",       // 한 단계 여유
};

export const DEFAULT_FONT_SCALE = fontScales.SMALL;

/**
 * 역할별 폰트 크기. CSS 가 닿는 곳은 전부 이걸 쓴다 — `sx={{ fontSize: FS.BODY }}`.
 * 숫자 px 를 새로 하드코딩하지 말 것. (아이콘 `fontSize` 는 텍스트가 아니라 레이아웃
 * 치수이므로 대상이 아니다.)
 */
export const FS = {
  CAPTION: "var(--fs-caption)",   // 최소 크기. 배지·칩·테이블 헤더·보조 라벨
  BODY: "var(--fs-body)",         // 기본 본문·필드·그리드 셀
  SUB: "var(--fs-sub)",           // 부제·설명·카테고리
  TITLE: "var(--fs-title)",       // 카드 제목
  H2: "var(--fs-h2)",             // 섹션 제목
  H1: "var(--fs-h1)",             // 페이지·히어로 타이틀
  DISPLAY: "var(--fs-display)",   // 수식·강조 숫자
  KPI: "var(--fs-kpi)",           // KPI 헤드라인 숫자
};

const isFontScale = (scale) => Object.values(fontScales).includes(scale);

/** 저장된 스케일. 없거나 값이 이상하면 기본값. */
export const getFontScale = () => {
  const saved = localStorage.getItem(FONT_SCALE_KEY);
  return isFontScale(saved) ? saved : DEFAULT_FONT_SCALE;
};

/**
 * 해석된 px 숫자.
 *
 * canvas 렌더러(ag-charts 등)는 `var()` 를 해석하지 못하고 값을 그대로 쓰기 때문에,
 * 그런 곳에서만 이 함수를 쓴다. CSS 가 닿는 곳은 FS 를 쓸 것.
 * (factory-plan 처럼 스코프에서 변수를 덮는 영역은 그 안의 element 를 넘겨야 맞는 값이 나온다.)
 */
export const getFontSizePx = (role, element = document.documentElement) =>
    parseFloat(getComputedStyle(element).getPropertyValue(`--fs-${role}`));

/**
 * <html> 의 data-font-scale 속성과 localStorage 를 갱신한다.
 * 실제 px 는 App.scss 의 `:root[data-font-scale="..."]` 가 결정한다.
 */
export const applyFontScale = (scale) => {
  const next = isFontScale(scale) ? scale : DEFAULT_FONT_SCALE;

  document.documentElement.dataset.fontScale = next;
  localStorage.setItem(FONT_SCALE_KEY, next);
  return next;
};
